document.addEventListener("alpine:init", () => {
  Alpine.data("shapeSelector", () => ({
    loading: true,
    shapes: [],
    currentShape: "",
    debug: false, // Set to true to show debug info

    // Get current page's selected variant
    getCurrentVariant() {
      // First try to get from Liquid data
      const currentVariantId = window.shapeSelectorData?.currentVariantId;
      if (currentVariantId) {
        return currentVariantId;
      }

      // Fallback to URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const variantIdFromUrl = urlParams.get("id");

      // If no URL param, try to get from Alpine data if available
      if (!variantIdFromUrl && window.Alpine && window.Alpine.store) {
        const productData = document.querySelector('[x-data*="product("]');
        if (productData && productData._x_dataStack) {
          const productComponent = productData._x_dataStack[0];
          if (productComponent && productComponent.selectedVariantID) {
            return productComponent.selectedVariantID;
          }
        }
      }

      return variantIdFromUrl;
    },

    // Determine shape type from shape value
    getShapeType(value) {
      if (!value) return "unknown";

      const normalizedValue = value.toLowerCase();

      // Define shape type mappings
      const shapeTypes = {
        rectangle: ["rectangle", "prostokątny", "rektangulär", "rectangulaire"],
        round: ["round", "rond", "rund", "okrągły"],
        oval: ["oval", "owalny", "ovale"],
        irregular: [
          "irregular",
          "nieregularny",
          "irréguliers",
          "oregelbunden form",
        ],
        square: ["square", "kwadratowy", "carré", "fyrkantig"],
        runner: ["runner", "tapis de couloir", "löpare", "chodnik dywanowy"],
      };

      // Find matching shape type
      for (const [type, terms] of Object.entries(shapeTypes)) {
        if (terms.some((term) => normalizedValue.includes(term))) {
          return type;
        }
      }

      return "unknown";
    },

    // Find matching variant in target product by title
    findMatchingVariant(targetProduct, currentVariantTitle) {
      if (!targetProduct.variants || !currentVariantTitle) return null;

      return targetProduct.variants.find(
        (variant) => variant.title === currentVariantTitle,
      );
    },

    // Find matching color in target product - updated to handle colors array
    findMatchingColor(targetProduct, currentColors) {
      if (
        !targetProduct.specifications?.colors ||
        !currentColors ||
        currentColors.length === 0
      )
        return false;

      // Check if any of the current colors match any color in the target product
      return currentColors.some((currentColor) =>
        targetProduct.specifications.colors.some(
          (targetColor) => targetColor.value === currentColor.value,
        ),
      );
    },

    // Get current variant title from current product
    getCurrentVariantTitle() {
      const currentVariantId = this.getCurrentVariant();
      if (!currentVariantId) return null;

      const collectionProducts = window.shapeSelectorData?.collectionProducts;
      if (!collectionProducts) return null;

      // Use current product URL from Liquid data if available
      const currentProductUrl =
        window.shapeSelectorData?.currentProductUrl ||
        window.location.pathname.replace(".html", "").replace("/", "");

      const currentProduct = collectionProducts.find(
        (p) => p.url === currentProductUrl,
      );

      if (!currentProduct || !currentProduct.variants) return null;

      const currentVariant = currentProduct.variants.find(
        (v) => String(v.externalId) === String(currentVariantId),
      );

      return currentVariant ? currentVariant.title : null;
    },

    // Get current colors from current product - updated to return array
    getCurrentColors() {
      const currentProductColors =
        window.shapeSelectorData?.currentProductColors;
      if (currentProductColors && currentProductColors.length > 0) {
        return currentProductColors;
      }
      return [];
    },

    // Handle click event to add scroll position to URL
    handleShapeClick(event, originalUrl) {
      event.preventDefault();

      const scrollTop = window.pageYOffset;
      let finalUrl = originalUrl;

      if (scrollTop > 0) {
        const url = new URL(originalUrl, window.location.origin);
        url.searchParams.set("scrollPos", String(scrollTop));
        finalUrl = url.toString();
      }

      window.location.href = finalUrl;
    },

    async initShapeSelector() {
      try {
        // Get current product shape from the global data passed from Liquid
        const currentProductShape =
          window.shapeSelectorData?.currentProductShape;
        if (currentProductShape?.value) {
          this.currentShape = currentProductShape.value;
        }

        // Collection products to fetch from global data
        const collectionProducts = window.shapeSelectorData?.collectionProducts;
        if (!collectionProducts || collectionProducts.length === 0) {
          this.loading = false;
          return;
        }
        // Create comma-separated list of product URLs
        const productUrls = collectionProducts.map((p) => p.url).join(",");

        // Call our server-side API endpoint (same as color selector)
        const response = await fetch(
          `/api/product/collection-colors?products=${encodeURIComponent(productUrls)}`,
        );

        if (!response.ok) {
          console.error(
            "Failed to fetch collection products:",
            response.status,
          );
          this.loading = false;
          return;
        }

        const apiData = await response.json();

        if (!apiData.success) {
          console.error("API returned error:", apiData.error);
          this.loading = false;
          return;
        }
        // Process the returned product details to extract shapes
        const shapeDetails = apiData.products
          .map((product) => {
            if (product.specifications.shape?.value) {
              return {
                url: product.url,
                shape: product.specifications.shape,
                title: product.title,
                colors: product.specifications.colors || [],
                variants: product.variants || [],
              };
            }
            return null;
          })
          .filter((detail) => detail !== null);

        // Process shapes
        const uniqueShapes = [];
        const currentVariantTitle = this.getCurrentVariantTitle();
        const currentColors = this.getCurrentColors();

        // Group products by shape to handle multiple products per shape
        const productsByShape = {};
        shapeDetails.forEach((detail) => {
          if (detail && detail.shape) {
            const shapeValue = detail.shape.value;
            if (!productsByShape[shapeValue]) {
              productsByShape[shapeValue] = [];
            }
            productsByShape[shapeValue].push(detail);
          }
        });

        // Process each unique shape
        Object.keys(productsByShape).forEach((shapeValue) => {
          const productsForShape = productsByShape[shapeValue];

          // Build URL with color and variant preservation (without scroll position - that's added on click)
          let finalUrl = productsForShape[0].url + ".html";

          // Try to preserve current color and variant when switching shapes
          if (currentColors.length > 0 || currentVariantTitle) {
            // Find the best product for this shape that matches our criteria
            let bestMatch = null;
            let bestScore = -1;

            productsForShape.forEach((detail) => {
              const targetProduct = apiData.products.find(
                (p) => p.url === detail.url,
              );

              if (targetProduct) {
                let score = 0;
                let selectedVariant = null;

                // Check for color match
                const hasMatchingColor = this.findMatchingColor(
                  targetProduct,
                  currentColors,
                );

                if (hasMatchingColor) {
                  score += 10; // High priority for color match

                  // Try to match variant size as well
                  if (currentVariantTitle) {
                    selectedVariant = this.findMatchingVariant(
                      targetProduct,
                      currentVariantTitle,
                    );
                    if (selectedVariant) {
                      score += 5; // Bonus for variant match
                    }
                  }
                } else if (currentVariantTitle) {
                  // Even without color match, try to match variant size
                  selectedVariant = this.findMatchingVariant(
                    targetProduct,
                    currentVariantTitle,
                  );
                  if (selectedVariant) {
                    score += 2; // Lower priority for size-only match
                  }
                }

                // Select this product if it has a better score
                if (score > bestScore) {
                  bestScore = score;
                  bestMatch = {
                    product: targetProduct,
                    detail: detail,
                    variant: selectedVariant,
                  };
                }
              }
            });

            // Use the best match if found
            if (bestMatch) {
              if (bestMatch.variant) {
                finalUrl = `${bestMatch.detail.url}.html?id=${bestMatch.variant.externalId}`;
              } else {
                finalUrl = bestMatch.detail.url + ".html";
              }
            }
          }

          uniqueShapes.push({
            value: shapeValue,
            url: finalUrl,
            isCurrent: shapeValue === this.currentShape,
          });
        });

        this.shapes = uniqueShapes;
        this.loading = false;
      } catch (error) {
        console.error("Error loading shape selector:", error);
        this.loading = false;
      }
    },
  }));
});
